import React, { useMemo, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import DeliveryIcon from '@common/components/icons/DeliveryIcon';
import ChevronIcon from '@common/components/icons/ChevronIcon';
import MultiSelectList from '@common/components/MultiSelectList';
import * as styles from '../ScheduleReportPage.module.css';

/**
 * DeliveryRecipientsSection Component
 *
 * Section for configuring delivery method and recipients
 */
function DeliveryRecipientsSection({
  expanded,
  onToggle,
  shareVia,
  onShareViaChange,
  recipients,
  selectedRecipients,
  onSelectionChange,
  activeRecipientFilters,
  onFilterToggle,
  loading,
}) {
  const { t } = useTranslation();

  // Filter options for recipients
  const recipientFilterOptions = useMemo(
    () => [
      {
        value: 'contact',
        label: t('scheduledReports.sections.deliveryRecipients.individualContacts'),
      },
      {
        value: 'contactGroup',
        label: t('scheduledReports.sections.deliveryRecipients.contactGroups'),
      },
    ],
    [t],
  );

  // Render badge for recipient type
  const renderRecipientBadge = useCallback(
    (recipient) => {
      const badges = [];

      // Type badge (Individual Contacts or Contact Groups)
      const typeBadgeClass = recipient.type === 'contactGroup' ? styles.typeBadgeGroup : styles.typeBadgeContact;
      const typeLabel =
        recipient.type === 'contactGroup'
          ? t('scheduledReports.recipientTypes.contactGroups')
          : t('scheduledReports.recipientTypes.individualContacts');

      badges.push(
        <span key="type" className={`${styles.recipientBadge} ${typeBadgeClass}`}>
          {typeLabel}
        </span>,
      );

      // Role badge (Admin/User) - only for contacts
      if (recipient.type === 'contact' && recipient.role) {
        const roleBadgeClass = recipient.role === 'Admin' ? styles.roleBadgeAdmin : styles.roleBadgeUser;
        badges.push(
          <span key="role" className={`${styles.recipientBadge} ${roleBadgeClass}`}>
            {recipient.role}
          </span>,
        );
      }

      return <div className={styles.badgesContainer}>{badges}</div>;
    },
    [t],
  );

  return (
    <div className={styles.section}>
      <button type="button" className={styles.sectionHeader} onClick={onToggle}>
        <div className={styles.sectionTitleWrapper}>
          <DeliveryIcon className={styles.sectionIcon} />
          <span className={styles.sectionTitle}>{t('scheduledReports.sections.deliveryRecipients.title')}</span>
        </div>
        <ChevronIcon className={expanded ? styles.chevronExpanded : styles.chevron} />
      </button>

      {expanded && (
        <div className={styles.sectionContent}>
          {/* Share Via - Email only */}
          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel}>{t('scheduledReports.sections.deliveryRecipients.shareVia')}</span>
            <div className={styles.checkboxGroup}>
              <label className={styles.checkboxLabel} htmlFor="share-via-email">
                <input
                  id="share-via-email"
                  type="checkbox"
                  checked={shareVia.email}
                  onChange={(e) => onShareViaChange({ ...shareVia, email: e.target.checked })}
                  className={styles.checkbox}
                />
                <span>{t('scheduledReports.sections.deliveryRecipients.email')}</span>
              </label>
            </div>
          </div>

          {/* Recipients */}
          {shareVia.email && (
            <MultiSelectList
              id="recipient-search"
              label={t('scheduledReports.sections.deliveryRecipients.recipients')}
              items={recipients}
              selectedIds={selectedRecipients}
              onSelectionChange={onSelectionChange}
              searchPlaceholder={t('scheduledReports.sections.deliveryRecipients.searchPlaceholder')}
              labelKey="name"
              descriptionKey="email"
              idKey="id"
              typeKey="type"
              loading={loading}
              emptyMessage={t('scheduledReports.sections.deliveryRecipients.noRecipients')}
              showChips
              filterOptions={recipientFilterOptions}
              activeFilters={activeRecipientFilters}
              onFilterChange={onFilterToggle}
              renderBadge={renderRecipientBadge}
              chipSubtitleKey="email"
              filterMode="radio"
              bothLabel={t('scheduledReports.sections.deliveryRecipients.both')}
            />
          )}
        </div>
      )}
    </div>
  );
}

DeliveryRecipientsSection.propTypes = {
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  shareVia: PropTypes.shape({
    email: PropTypes.bool,
  }).isRequired,
  onShareViaChange: PropTypes.func.isRequired,
  recipients: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      email: PropTypes.string,
      type: PropTypes.string,
      role: PropTypes.string,
    }),
  ).isRequired,
  selectedRecipients: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  onSelectionChange: PropTypes.func.isRequired,
  activeRecipientFilters: PropTypes.arrayOf(PropTypes.string).isRequired,
  onFilterToggle: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

DeliveryRecipientsSection.defaultProps = {
  loading: false,
};

export default memo(DeliveryRecipientsSection);
